import { ChevronRight } from 'lucide-react';

interface FolderBreadcrumbProps {
    path?: string | null;
    /** Called when a segment is clicked. Passes the truncated path up to that segment. */
    onNavigate?: (path: string) => void;
}

/**
 * Renders a materialized folder path as a clickable breadcrumb trail.
 * e.g. "/root/Login/Happy Path"  →  Root  /  Login  /  Happy Path
 */
export function FolderBreadcrumb({ path, onNavigate }: FolderBreadcrumbProps) {
    if (!path) return null;

    // Split "/root/Login/Happy Path" → ["Root", "Login", "Happy Path"]
    const raw = path.split('/').filter(Boolean);
    const segments = raw.map((seg, i) => ({
        label:    i === 0 ? 'Root' : seg,
        truncated: '/' + raw.slice(0, i + 1).join('/'),
    }));

    return (
        <nav aria-label="Folder path" className="flex items-center gap-0.5 flex-wrap">
            {segments.map((seg, i) => (
                <span key={seg.truncated} className="flex items-center gap-0.5">
                    {i > 0 && <ChevronRight size={11} className="text-slate-400 flex-shrink-0" />}
                    <button
                        type="button"
                        data-testid={`folder-breadcrumb-segment-${seg.label.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => onNavigate?.(seg.truncated)}
                        className="text-xs text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                        {seg.label}
                    </button>
                </span>
            ))}
        </nav>
    );
}
